<template>
  <div class="settings-org-section settings-change-history-section">
    <SettingsSectionHeader
      title="Change Log"
      subtitle="Append-only field-level changes for leads, onboarding, and drivers"
      icon="pi-history"
    >
      <template #actions>
        <div class="settings-change-history-section__view-toggle" title="History view mode">
          <button
            type="button"
            class="settings-change-history-section__toggle-btn"
            :class="{ 'is-active': viewMode === 'flat' }"
            @click="setViewMode('flat')"
          >
            Flat list
          </button>
          <button
            type="button"
            class="settings-change-history-section__toggle-btn"
            :class="{ 'is-active': viewMode === 'grouped' }"
            @click="setViewMode('grouped')"
          >
            Timeline
          </button>
        </div>
        <FilterPresetsControl
          :presets="filterPresets.presets"
          :selected-preset-id="filterPresets.selectedPresetId"
          :loading-presets="filterPresets.loadingPresets"
          :saving-preset="filterPresets.savingPreset"
          @apply="filterPresets.applyPreset"
          @save="filterPresets.saveCurrentPreset"
          @delete="filterPresets.removePreset"
        />
        <Button
          type="button"
          label="Refresh"
          icon="pi pi-refresh"
          size="small"
          severity="secondary"
          outlined
          :loading="loading"
          @click="reload"
        />
      </template>
    </SettingsSectionHeader>

    <div class="settings-org-section__body settings-change-history-section__body">
      <Message v-if="error" severity="error" :closable="false" class="settings-org-section__message">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
          <span class="flex-1">{{ error }}</span>
          <Button label="Retry" size="small" severity="danger" outlined @click="reload" />
        </div>
      </Message>

      <HistoryFiltersBar
        :search="filters.search"
        :ref-type="filters.refType"
        :date-from="filters.dateFrom"
        :date-to="filters.dateTo"
        @update:search="filters.search = $event"
        @update:ref-type="filters.refType = $event"
        @update:date-from="filters.dateFrom = $event"
        @update:date-to="filters.dateTo = $event"
        @search-debounced="reload"
        @filter-change="reload"
        @clear="clearFilters"
      />

      <p v-if="loadedOnce" class="settings-change-history-section__count text-sm text-color-secondary">
        {{ totalLabel }}
      </p>

      <HistoryFlatTable
        v-if="viewMode === 'flat'"
        :rows="flatRows"
        :loading="loading"
      />
      <HistoryTimeline
        v-else
        :groups="groupedGroups"
        :loading="loading"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import Button from 'primevue/button';
import Message from 'primevue/message';
import SettingsSectionHeader from '../SettingsSectionHeader.vue';
import FilterPresetsControl from '@/features/drivers/components/shared/FilterPresetsControl.vue';
import { useFilterPresets } from '@/features/drivers/composables/useFilterPresets';
import { useHistoryList } from '@/features/drivers/composables/useHistoryList';
import HistoryFiltersBar from '@/features/drivers/components/history/HistoryFiltersBar.vue';
import HistoryFlatTable from '@/features/drivers/components/history/HistoryFlatTable.vue';
import HistoryTimeline from '@/features/drivers/components/history/HistoryTimeline.vue';

defineOptions({ name: 'SettingsChangeHistorySection' });

const {
  loading,
  loadedOnce,
  error,
  flatRows,
  groupedGroups,
  total,
  viewMode,
  filters,
  loadList,
  reload,
  clearFilters,
  setViewMode
} = useHistoryList();

const filterPresets = useFilterPresets('history', {
  getFilters: () => ({
    search: filters.search,
    refType: filters.refType,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo
  }),
  applyFilters: (saved) => {
    filters.search = saved?.search || '';
    filters.refType = saved?.refType || '';
    filters.dateFrom = saved?.dateFrom || '';
    filters.dateTo = saved?.dateTo || '';
    reload();
  }
});

const totalLabel = computed(() => {
  if (viewMode.value === 'grouped') {
    return `${total.value} record group${total.value === 1 ? '' : 's'}`;
  }
  return `${total.value} change${total.value === 1 ? '' : 's'}`;
});

onMounted(() => {
  loadList();
  filterPresets.loadPresets();
});
</script>

<style scoped>
.settings-change-history-section__body {
  overflow-y: auto;
}

.settings-change-history-section__count {
  margin: 0;
}

.settings-change-history-section__view-toggle {
  display: inline-flex;
  border: 1px solid var(--crc-panel-border, var(--surface-border));
  border-radius: 0.5rem;
  overflow: hidden;
}

.settings-change-history-section__toggle-btn {
  padding: 0.375rem 0.75rem;
  border: 0;
  background: transparent;
  color: var(--crc-text-muted, var(--text-color-secondary));
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}

.settings-change-history-section__toggle-btn.is-active {
  background: var(--crc-green-soft, rgba(29, 158, 117, 0.12));
  color: var(--crc-green, #1d9e75);
}

.settings-change-history-section__toggle-btn:not(:last-child) {
  border-right: 1px solid var(--crc-panel-border, var(--surface-border));
}
</style>
